import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolCard } from '../../types/agent';
import { toolLabel } from '../../services/agent/toolCatalogue';
import { qualify } from '../../services/excel/a1';
import { describeInput, type ActionCardActions } from './ActionCard';
import { Collapse, useLingering } from './motion';

/** The edits of one batch, approved as one (`ChatSession.attachApproval`): how many and on which sheets, each edit
 *  one disclosure away, then Accept edits or Reject — never some of them. A Change Plan is one change, written,
 *  verified and undone as one, and a manual write is declared as one; approving part of either leaves the workbook
 *  in a state nobody planned. Rejecting asks what should change, and that goes back to the agent with the refusal. */
export function BatchApprovalCard({ card, actions }: { card: ToolCard; actions: ActionCardActions }) {
  const { t } = useTranslation();
  const edits = editsOf(card);
  const sheets = [...new Set(edits.map((e) => e.target?.sheet).filter((s): s is string => !!s))];
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const settled = card.status !== 'awaiting_confirmation';
  const cells = edits.reduce((n, e) => n + (e.changes?.cells ?? 0), 0);
  const failed = edits.filter((e) => e.status === 'error').length;
  const waiting = card.status === 'awaiting_confirmation';
  const body = (
    <div className={`overflow-hidden rounded-lg border bg-white ${card.risk === 'destructive' && waiting ? 'border-[#e8c9c5]' : 'border-line-card'}`} data-testid="batch-approval">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center justify-between gap-2 border-b border-line-row px-3 py-[9px] text-left hover:bg-hover">
        <span className="min-w-0 truncate text-[12px] font-medium text-ink">
          {t('approval.title', { count: edits.length })}
          {sheets.length > 0 && <span className="font-normal text-muted"> · {sheets.join(', ')}</span>}
        </span>
        <span className="shrink-0 text-[11px] text-muted">{open ? t('approval.hide') : t('approval.show')}</span>
      </button>
      {open && (
        <ol className="m-0 flex max-h-[320px] list-none flex-col overflow-y-auto p-0" data-testid="batch-edits">
          {edits.map((e) => (
            <li key={e.tool_call_id} className="border-b border-line-row px-3 py-2 last:border-b-0">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-[11.5px] text-ink" title={e.preview || undefined}>{e.preview || toolLabel(e.tool_name)}</span>
                {e.target && <span className="shrink-0 font-mono text-[11px] text-muted">{qualify(e.target.sheet, e.target.address)}</span>}
              </div>
              <pre className="m-0 mt-1 whitespace-pre-wrap break-all rounded-sm bg-code px-2 py-1.5 font-mono text-[11px] leading-normal text-[#2d3134]">{describeInput(e)}</pre>
            </li>
          ))}
        </ol>
      )}
      {card.risk === 'destructive' && <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-danger">{t('action.destructiveWarning')}</div>}
      {card.protected && <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-danger">{t('action.protectedSheet')}</div>}
      {rejecting ? (
        <div className="flex flex-col gap-1.5 border-t border-line-row p-2">
          <textarea autoFocus rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('approval.reasonPlaceholder')} className="w-full resize-none rounded-[5px] border border-[#dcdcd8] px-2 py-1.5 text-[12px] text-ink outline-hidden focus:border-accent" />
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => actions.onDecide(card.tool_call_id, false, { message: reason })} className="flex-1 rounded-[5px] border border-[#dcdcd8] py-[7px] text-center text-[12.5px] font-medium text-ink transition-colors hover:bg-hover">
              {t('approval.confirmReject')}
            </button>
            <button type="button" onClick={() => setRejecting(false)} className="rounded-[5px] px-3 py-[7px] text-[12.5px] text-muted transition-colors hover:text-ink">
              {t('approval.back')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 border-t border-line-row p-2">
          <button type="button" onClick={() => actions.onDecide(card.tool_call_id, true)} className="flex-1 rounded-[5px] bg-accent py-[7px] text-center text-[12.5px] font-medium text-white transition-colors hover:bg-accent-hover">
            {t('approval.accept')}
          </button>
          <button type="button" onClick={() => setRejecting(true)} className="rounded-[5px] border border-[#dcdcd8] px-3 py-[7px] text-[12.5px] text-[#4a4f54] transition-colors hover:bg-hover">
            {t('approval.reject')}
          </button>
        </div>
      )}
    </div>
  );
  const lingering = useLingering(!settled, body);
  if (settled) {
    return (
      <div className="flex flex-col" data-card-status={card.status}>
        {lingering && <Collapse leaving>{lingering}</Collapse>}
        <div className="flex min-h-[26px] animate-pc-in-enter items-center gap-2 rounded-md bg-code px-2 py-1 text-[12px]">
          {card.status === 'cancelled' ? (
            <span className="text-faint">{t('approval.rejected')}</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-accent-dark">
              <span className="inline-block animate-pc-pop">✓</span> {t('approval.accepted', { count: edits.length })}
              {cells > 0 && <span>· {t('action.cells', { count: cells })}</span>}
              {failed > 0 && <span className="text-warn">· {t('approval.failed', { count: failed })}</span>}
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="animate-pc-in-enter" data-card-status={card.status}>
      {body}
    </div>
  );
}

function editsOf(card: ToolCard): ToolCard[] {
  const edits = (card.input as { edits?: unknown }).edits;
  return Array.isArray(edits) ? (edits as ToolCard[]) : [];
}
